import {
  DEFAULT_PAGINATION_PARAM_LIMIT,
  DEFAULT_PAGINATION_PARAM_OFFSET
} from '../../../clients/oneCloud/relationshipMgt/constants';
import OneCloudAggregationSvcApiClient from '../../../clients/oneCloud/relationshipMgt/OneCloudAggregationSvcApiClient';
import OnecloudOauthClient from '../../../clients/oneCloud/oauth/OnecloudOauthClient';
import { OneCloudAPIDefaultRequestParams } from '../../../clients/oneCloud/types';
import { AuthContextEnum } from '../../authTokenService';
import { ISessionService } from '../../session';
import TenantFilterBuilder from '../tenantFilterBuilder/TenantFilterBuilder';
import TenantUtils from '../TenantUtils';
import { TenantStrategyEnum } from './strategy';
import TenantStrategy from './TenantStrategy';
import BaseTenantHandler from './BaseTenantHandler';
import * as T from './types';
import {
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  DEFAULT_PAGINATION
} from './constants';

type OrganizationProperties = {
  fallback?: string;
  filter?: {
    role: 'admin' | 'user' | 'all';
    tenantType: string[];
  };
  customBehaviors?: [
    {
      filter?: {
        role: 'admin' | 'user' | 'all';
        tenantType: string[];
      };
      onSelect: {
        redirectTo?: string;
      };
    }
  ];
};

/**
 * OneCloud Organization Tenant Handler.
 * Manages tenant operations for OneCloud organization contexts.
 */
export default class OnecloudOrganizationTenantHandler
  extends BaseTenantHandler
  implements TenantStrategy
{
  private _strategy = TenantStrategyEnum.onecloudOrganization;
  private _options: OrganizationProperties;
  private _assetReference: string;
  private _oneCloudAggregationSvcClient: OneCloudAggregationSvcApiClient;
  private _onecloudOauthClient: OnecloudOauthClient;
  private _sessionService: ISessionService;

  constructor({
    stack,
    options,
    assetReference,
    sessionService,
    authProvider,
    tenantsDataRepository,
    eventService
  }: T.TenantHandlerStrategyType) {
    // Call parent constructor with common dependencies
    super({
      stack,
      authProvider,
      tenantsDataRepository,
      eventService
    });

    // Initialize OneCloud-specific dependencies
    this._options = options;
    this._assetReference = assetReference;
    this._sessionService = sessionService;

    this._oneCloudAggregationSvcClient = new OneCloudAggregationSvcApiClient(
      OneCloudAggregationSvcApiClient.getBaseUrl(this._stack),
      this._authProvider
    );

    this._onecloudOauthClient = new OnecloudOauthClient({
      stack: this._stack,
      authProvider: this._authProvider
    });
  }

  // Methods from TenantStrategy interface
  public getAssetReference(): string {
    return this._assetReference;
  }

  public getStrategy = (): TenantStrategyEnum => {
    return this._strategy;
  };

  public setTenant = async (
    id: string,
    authContext: AuthContextEnum
  ): Promise<void> => {
    await this._sessionService.exchangeToken(
      id,
      authContext,
      TenantStrategyEnum.onecloudOrganization
    );
  };

  public isRequiredToNavigate = (): boolean => {
    return true;
  };

  public getFallback = (): string => {
    return this?._options?.fallback;
  };

  public isTenantValid(
    filter: T.TenantHandlerFilterType,
    tenant: T.TenantVisualizationType
  ): boolean {
    return this.getFilteredTenantList(filter, [tenant])?.length > 0;
  }

  public async createTenant(
    options: T.CreateTenantStrategyOptionsType
  ): Promise<T.CreateTenantResultType> {
    const response = await this._onecloudOauthClient.createStratusAccount({
      accountId: undefined,
      accountName: options?.accountName,
      language: options?.language || DEFAULT_LANGUAGE,
      countrySet: options?.countrySet || [DEFAULT_COUNTRY],
      idToken: options.idToken,
      type: options?.type,
      parentAccountResourceId: options.parentAccountResourceId
    });

    return { resourceId: response.resourceId };
  }

  // Implementation of abstract method from BaseTenantHandler
  public async _getPaginatedTenantListData(
    options: T.GetTenantListOptionsType
  ): Promise<T.PaginatedTenantVisualizationType> {
    const { page, paginationSize } = options;
    const _options: OneCloudAPIDefaultRequestParams = {};
    const limit = paginationSize || DEFAULT_PAGINATION_PARAM_LIMIT;
    const offset = page ? limit * page : DEFAULT_PAGINATION_PARAM_OFFSET;
    _options.params = {
      limit,
      offset
    };

    const personOrgs = await this._oneCloudAggregationSvcClient.listPersonOrgs(
      _options
    );

    const tenants = personOrgs?.role_orgs?.map((tenantData) =>
      TenantUtils._convertOnecloudOrgToTenantVisualizationType(tenantData)
    );

    const totalPages = Math.ceil(
      parseInt(personOrgs.params.db_total) / DEFAULT_PAGINATION_PARAM_LIMIT
    );

    const { filter } = this._options;
    const filteredTenantList = this.getFilteredTenantList(filter, tenants);
    const customBehaviorsList = this._options?.customBehaviors;
    if (customBehaviorsList) {
      let unifiedTenantList = filteredTenantList;
      unifiedTenantList = this.addCustomBehaviorsTenantList(
        customBehaviorsList,
        unifiedTenantList,
        tenants
      );
      return {
        totalPages: totalPages || DEFAULT_PAGINATION.CURRENT_PAGE,
        currentPage: page || DEFAULT_PAGINATION.CURRENT_PAGE,
        tenants: unifiedTenantList
      };
    }
    //TODO console.warn to indicate that this list have only the nodes that were found by the pagination one
    return {
      totalPages: totalPages || DEFAULT_PAGINATION.CURRENT_PAGE,
      currentPage: page || DEFAULT_PAGINATION.CURRENT_PAGE,
      tenants: filteredTenantList
    };
  }

  private getFilteredTenantList(
    filter: T.TenantHandlerFilterType,
    tenants: T.TenantVisualizationType[]
  ) {
    return new TenantFilterBuilder({
      filter,
      tenants
    })
      .filterByRegexTenantType()
      .filterByRole()
      .filterByWhiteList()
      .getTenantList();
  }

  /**
   * Adds custom behaviors to the tenant list based on configuration
   * @param customBehaviorsList - List of custom behaviors to apply
   * @param tenantList - Initial tenant list
   * @param tenants - Complete list of tenants
   * @returns Merged and deduplicated tenant list
   */
  private addCustomBehaviorsTenantList(
    customBehaviorsList: OrganizationProperties['customBehaviors'],
    tenantList: T.TenantVisualizationType[],
    tenants: T.TenantVisualizationType[]
  ): T.TenantVisualizationType[] {
    if (!customBehaviorsList || !tenantList || !tenants) {
      return tenantList;
    }

    let resultList = tenantList;

    for (const customBehaviors of customBehaviorsList) {
      const filterCustomBehavior = customBehaviors.filter;

      // Grant filters can't be resolved synchronously here; they're only
      // checked at select-time (TenantHandlerService), so skip them to
      // avoid matching every tenant when tenantType/role aren't also set.
      if ((filterCustomBehavior as T.TenantHandlerFilterType)?.grant) {
        continue;
      }

      const filteredCustomBehaviors = this.getFilteredTenantList(
        filterCustomBehavior,
        tenants
      );

      // Call inherited method from BaseTenantHandler to merge and deduplicate tenant lists
      resultList = this.mergeListsAndRemoveDuplicates(
        resultList,
        filteredCustomBehaviors
      );
    }

    return resultList;
  }
}
